import React, { useCallback, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  Avatar,
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import Timeline from '@mui/icons-material/Timeline';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import BarChart from '@mui/icons-material/BarChart';
import SmartDisplayOutlined from '@mui/icons-material/SmartDisplayOutlined';
import InstallMobileOutlined from '@mui/icons-material/InstallMobileOutlined';
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Logout from '@mui/icons-material/Logout';

import { AddVideoButton } from '../AddVideoButton';
import { AuthModal } from '../AuthModal';
import { NotificationBell } from '../NotificationBell';
import { RankDialog } from '../RankDialog';
import { Toast } from '../Toast';
import { SideNavFooter } from '../SideNavFooter';
import { User, UserRoles } from '../../types';
import {
  authModalAtom,
  isMobileAtom,
  notificationsOpenAtom,
  pickedVideoFileAtom,
  toastAtom,
} from '../../atoms';
import { StateAuth } from '../../context/auth';
import useOpenReportsCount from '../../hooks/useOpenReportsCount';
import useRequireAuth from '../../hooks/useRequireAuth';
import {
  menuItem,
  navCount,
  navIcon,
  navIconActive,
  navIndicator,
  navItem,
  navItemActive,
  navItemStep,
  navLabel,
} from '../../theme';
import styles from './style';
import { getAvatarUrl } from '../../helpers';

interface LayoutProps {
  children: React.ReactNode;
  logout: () => Promise<void>;
  user: User;
}

const AUTH_PAGES = ['/login', '/signup', '/forgotpassword'];
const MY_VIDEOS_PATH = '/my-videos';

interface NavItem {
  icon: React.ReactNode;
  label: string;
  path: string;
  // Red counter on the right (ex: the open reports).
  count?: number;
}

// The list of the menu in order: the pages, and for a user "Publier" and the notifications among them.
type NavEntry =
  | { kind: 'link'; item: NavItem }
  | { kind: 'publish' }
  | { kind: 'notifications' };

const Layout: React.FC<LayoutProps> = React.memo((props) => {
  const { children, logout, user } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { openAuthModal } = useRequireAuth();
  // On a phone only the admin comes here, for the dashboard alone: no side menu.
  const isMobile = useAtomValue(isMobileAtom);
  const setAuthModal = useSetAtom(authModalAtom);
  const setToast = useSetAtom(toastAtom);
  const setPickedVideoFile = useSetAtom(pickedVideoFileAtom);
  const notificationsOpen = useAtomValue(notificationsOpenAtom);
  // A visitor who is not logged in only has the feed.
  const isGuest = user.isConnected !== StateAuth.CONNECTED;
  const isShortLink = location.pathname.startsWith('/short/');
  const isAdmin = user.role === UserRoles.ADMIN;
  const isUser = !isGuest && !isAdmin;
  const openReports = useOpenReportsCount(isAdmin && !isMobile);
  // On the login, the sign up or the forgot password page, the buttons to log in are the page itself.
  const isAuthPage = AUTH_PAGES.includes(location.pathname);
  const avatarUrl = useMemo(() => getAvatarUrl(user.avatar), [user.avatar]);

  // An admin sees everything: he publishes from Vidéos ("Ajouter une vidéo"), for any account.
  const navItems = useMemo((): NavItem[] => {
    if (isGuest) {
      return [
        { icon: <HomeOutlined />, label: t('admin.nav.feed'), path: '/feed' },
        { icon: <BarChart />, label: t('admin.nav.ranking'), path: '/ranking' },
      ];
    }
    if (isAdmin) {
      return [
        { icon: <Timeline />, label: t('admin.nav.dashboard'), path: '/dashboard' },
        { icon: <InstallMobileOutlined />, label: t('admin.nav.installs'), path: '/installs' },
        { icon: <PeopleAltOutlined />, label: t('admin.nav.users'), path: '/users' },
        { icon: <SmartDisplayOutlined />, label: t('admin.nav.videos'), path: '/videos' },
        { icon: <FlagOutlined />, label: t('admin.nav.reports'), path: '/reports', count: openReports },
        { icon: <NotificationsNoneOutlined />, label: t('admin.nav.notifications'), path: '/notifications' },
        { icon: <HomeOutlined />, label: t('admin.nav.feed'), path: '/feed' },
        // Every generated week, with the details of each video (the story page /ranking is for the users).
        { icon: <BarChart />, label: t('admin.nav.rankings'), path: '/rankings' },
      ];
    }
    // "Mes vidéos" is the profile of the user (like TikTok and Instagram), "Profil" his settings.
    return [
      { icon: <HomeOutlined />, label: t('admin.nav.feed'), path: '/feed' },
      { icon: <BarChart />, label: t('admin.nav.ranking'), path: '/ranking' },
      { icon: <SmartDisplayOutlined />, label: t('admin.nav.myVideos'), path: MY_VIDEOS_PATH },
    ];
  }, [isAdmin, isGuest, openReports, t]);

  const openMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // The visitor lands on the feed, with no login popup left open by the page he was on.
  const onLogout = useCallback(async () => {
    setAnchorEl(null);
    await logout();
    setAuthModal(null);
    navigate('/feed', { replace: true });
  }, [logout, navigate, setAuthModal]);

  // The logo goes to the first page of the menu, never to "/": "/" is the public home,
  // it was rendered once (heavy, a freeze) before the redirect. Nothing to do when already there.
  const homePath = navItems[0].path;
  const goHome = useCallback(() => {
    if (location.pathname !== homePath) {
      navigate(homePath);
    }
  }, [homePath, location.pathname, navigate]);

  // The chevron turns over while the menu is open, then comes back.
  const chevronSx = useMemo(() => {
    if (anchorEl) {
      return { ...styles.chevron, ...styles.chevronOpen };
    }
    return styles.chevron;
  }, [anchorEl]);

  const openLogin = useCallback(() => {
    openAuthModal('login');
  }, [openAuthModal]);

  const openSignup = useCallback(() => {
    openAuthModal('signup');
  }, [openAuthModal]);

  // "Publier": the file is checked here, then the popup opens on Mes vidéos, where the grid is.
  const onVideoPicked = useCallback((file: File) => {
    setPickedVideoFile(file);
    if (location.pathname !== MY_VIDEOS_PATH) {
      navigate(MY_VIDEOS_PATH);
    }
  }, [location.pathname, navigate, setPickedVideoFile]);

  const onPickError = useCallback((text: string) => {
    setToast({ id: Date.now(), text, severity: 'warning' });
  }, [setToast]);

  const entries = useMemo((): NavEntry[] => {
    const links = navItems.map((item): NavEntry => ({ kind: 'link', item }));
    if (!isUser) {
      return links;
    }
    return [
      ...links.slice(0, 2),
      { kind: 'publish' },
      ...links.slice(2),
      { kind: 'notifications' },
      {
        kind: 'link',
        item: {
          icon: <Avatar alt="" src={avatarUrl} sx={styles.navAvatar} />,
          label: t('admin.nav.profile'),
          path: '/profile',
        },
      },
    ];
  }, [avatarUrl, isUser, navItems, t]);

  // A shared link (/short/…) is the feed.
  const isActivePath = useCallback((path: string) => (
    location.pathname === path || location.pathname.startsWith(`${path}/`) || (path === '/feed' && isShortLink)
  ), [isShortLink, location.pathname]);

  // The highlight slides to the active item, through the ones between. While the notifications
  // panel is open it sits on its item, the page stays where it is behind.
  const activeIndex = entries.findIndex((entry) => (
    notificationsOpen ? entry.kind === 'notifications' : entry.kind === 'link' && isActivePath(entry.item.path)
  ));
  const indicatorSx = useMemo(() => ({
    ...navIndicator,
    opacity: activeIndex < 0 ? 0 : 1,
    transform: `translateY(${Math.max(0, activeIndex) * navItemStep}px)`,
  }), [activeIndex]);

  const renderLink = useCallback((item: NavItem) => {
    const active = !notificationsOpen && isActivePath(item.path);
    return (
      <NavLink key={item.path} to={item.path} style={styles.navLink}>
        <Box sx={active ? navItemActive : navItem}>
          <Box sx={active ? navIconActive : navIcon}>
            {item.icon}
          </Box>
          <Box component="span" sx={navLabel}>{item.label}</Box>
          {!!item.count && <Box component="span" sx={navCount}>{item.count > 99 ? '99+' : item.count}</Box>}
        </Box>
      </NavLink>
    );
  }, [isActivePath, notificationsOpen]);

  const logoText = isAdmin ? 'ADMIN' : 'SOSKILLS';

  if (isMobile) {
    return (
      <Box sx={styles.rootMobile}>
        <Box component="header" sx={styles.topBarMobile}>
          <Box sx={styles.logo} onClick={goHome}>
            <Box component="img" src="/logo-black.png" alt="" sx={styles.logoImgMobile} />
            <Typography sx={styles.logoText}>{logoText}</Typography>
          </Box>
          {!isGuest && (
            <Button disableRipple onClick={openMenu} sx={styles.meButton}>
              <Avatar alt="" src={avatarUrl} sx={styles.meAvatar} />
              {user.username}
              <KeyboardArrowDown fontSize="small" sx={chevronSx} />
            </Button>
          )}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeMenu}
            anchorOrigin={styles.menuAnchorOrigin}
            transformOrigin={styles.menuTransformOrigin}
            PaperProps={styles.menuPaperProps}
          >
            <MenuItem onClick={onLogout} sx={menuItem}>
              <Logout fontSize="small" />
              {t('admin.nav.logout')}
            </MenuItem>
          </Menu>
        </Box>
        <Box component="main" sx={styles.mainMobile}>
          {children}
        </Box>
        <RankDialog />
        <Toast />
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <Box component="nav" sx={styles.sideNav}>
        {/* Same word in every language: "Admin" next to the logo tells the admin space from the one of a user. */}
        <Box sx={styles.navLogo} onClick={goHome}>
          <Box component="img" src="/logo-black.png" alt="" sx={styles.logoImg} />
          <Typography sx={styles.logoText}>{logoText}</Typography>
        </Box>
        <Box sx={styles.navList}>
          <Box sx={indicatorSx} />
          {entries.map((entry) => {
            if (entry.kind === 'publish') {
              return <AddVideoButton key="publish" nav label={t('admin.nav.publish')} onPicked={onVideoPicked} onError={onPickError} />;
            }
            if (entry.kind === 'notifications') {
              return <NotificationBell key="notifications" />;
            }
            return renderLink(entry.item);
          })}
        </Box>
        <Box sx={styles.navBottom}>
          {isGuest && !isAuthPage && (
            <Box sx={styles.guestActions}>
              <Button disableRipple onClick={openSignup} sx={styles.guestSignup}>{t('admin.guest.signUp')}</Button>
              <Button disableRipple onClick={openLogin} sx={styles.guestLogin}>{t('admin.guest.signIn')}</Button>
            </Box>
          )}
          {!isGuest && (
            <Box sx={styles.navList}>
              {isAdmin && (
                <Box sx={styles.navMe}>
                  <Box sx={navIcon}><Avatar alt="" src={avatarUrl} sx={styles.navAvatar} /></Box>
                  <Box component="span" sx={navLabel}>{user.username}</Box>
                </Box>
              )}
              <Box component="button" type="button" onClick={onLogout} sx={navItem}>
                <Box sx={navIcon}><Logout /></Box>
                <Box component="span" sx={navLabel}>{t('admin.nav.logout')}</Box>
              </Box>
            </Box>
          )}
          <SideNavFooter />
        </Box>
      </Box>
      <Box component="main" sx={styles.main}>
        {children}
      </Box>
      {isGuest && <AuthModal />}
      {/* Opened from any rank badge (rankDialogAtom). */}
      <RankDialog />
      {/* Opened from anywhere (toastAtom). */}
      <Toast />
    </Box>
  );
});

export default Layout;
